import { useMemo, useRef, useState } from 'react';
import {
  GRADE_LEVELS,
  MAX_STUDENTS,
  createEmptyStudent,
  studentLimitMessage,
} from '../constants/formConfig';
import {
  downloadStudentTemplate,
  parseStudentSpreadsheet,
} from '../utils/spreadsheet';
import {
  countSelectedTests,
  formatCurrency,
  getStudentPrice,
} from '../utils/pricing';
import { Field } from './Field';

export function StudentRegistration({ form, errors, onChange }) {
  const fileInputRef = useRef(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [fileName, setFileName] = useState('');

  const computedTestCount = useMemo(
    () =>
      form.students.reduce(
        (sum, student) => sum + countSelectedTests(student),
        0,
      ),
    [form.students],
  );

  const enteredStudentCount = useMemo(
    () =>
      form.students.filter(
        (student) =>
          student.firstName?.trim() ||
          student.lastName?.trim() ||
          student.gradeLevel ||
          student.tests?.reasoning ||
          student.tests?.computation,
      ).length,
    [form.students],
  );

  const updateStudent = (index, patch) => {
    const students = form.students.map((student, i) =>
      i === index ? { ...student, ...patch } : student,
    );
    onChange({ students });
  };

  const updateStudentTest = (index, testId, checked) => {
    const students = form.students.map((student, i) => {
      if (i !== index) return student;
      return {
        ...student,
        tests: { ...student.tests, [testId]: checked },
      };
    });
    onChange({ students });
  };

  const addStudent = () => {
    onChange({ students: [...form.students, createEmptyStudent()] });
  };

  const removeStudent = (index) => {
    if (form.students.length === 1) {
      onChange({ students: [createEmptyStudent()] });
      return;
    }
    onChange({ students: form.students.filter((_, i) => i !== index) });
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadErrors([]);
    setUploadMessage('');
    setFileName(file.name);

    try {
      const { students, errors: parseErrors } =
        await parseStudentSpreadsheet(file);

      const warnings = [...parseErrors];

      const testCount = students.reduce(
        (sum, student) => sum + countSelectedTests(student),
        0,
      );

      onChange({
        registeringStudents: 'yes',
        registrationMethod: 'bulk',
        students,
        totalTestCount: String(testCount),
      });
      setUploadErrors(warnings);
      setUploadMessage(
        `Loaded ${students.length} student${students.length === 1 ? '' : 's'} from “${file.name}”. Review the preview below before registering.`,
      );
    } catch (error) {
      setUploadErrors([error.message || 'Upload failed.']);
    }
  };

  return (
    <section className="section">
      <h2 className="section-title">Student Registrations</h2>

      <div className="student-count-readout">
        <span className="field-label">Students entered</span>
        <span className="student-count-value">{enteredStudentCount}</span>
      </div>
      {enteredStudentCount > MAX_STUDENTS ? (
        <p className="field-error" role="alert">
          {studentLimitMessage(enteredStudentCount)}
        </p>
      ) : null}

      <div style={{ marginTop: 16 }}>
        <Field required error={errors.registrationMethod}>
          <div className="field-label-row">
            <span className="field-label">
              Registration Method
              <span className="req">*</span>
            </span>
            {form.registrationMethod === 'bulk' ? (
              <button
                type="button"
                className="template-link-inline"
                onClick={() =>
                  downloadStudentTemplate().catch((err) =>
                    setUploadErrors([
                      err?.message || 'Could not generate the template.',
                    ]),
                  )
                }
              >
                (download template)
              </button>
            ) : null}
          </div>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="registrationMethod"
                value="manual"
                checked={form.registrationMethod === 'manual'}
                onChange={() => onChange({ registrationMethod: 'manual' })}
              />
              Manual Entry
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="registrationMethod"
                value="bulk"
                checked={form.registrationMethod === 'bulk'}
                onChange={() => onChange({ registrationMethod: 'bulk' })}
              />
              Bulk Upload
            </label>
          </div>
          <p className="field-help">
            <strong>Registering more than {MAX_STUDENTS} students?</strong> Use
            this registration page to register your first {MAX_STUDENTS}{' '}
            students. Then,{' '}
            <strong>you must use the link in your confirmation email</strong> to
            register additional students.
          </p>
        </Field>
      </div>

      {form.registrationMethod === 'bulk' ? (
        <div className="bulk-upload-row" style={{ marginTop: 16 }}>
          <div className="file-choose-row">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </button>
            <span className="file-name">
              {fileName || 'No file chosen'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
          <p className="field-help">
            Upload the completed spreadsheet, then confirm the total test count
            below.
          </p>
          {uploadMessage ? (
            <p className="upload-success">{uploadMessage}</p>
          ) : null}
          {uploadErrors.length ? (
            <div className="upload-errors">
              <strong>Please review these spreadsheet warnings:</strong>
              <ul>
                {uploadErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        className={`field field-inline ${
          errors.totalTestCount ? 'has-error' : ''
        }`}
        style={{ marginTop: 16, display: 'none'}}
      >
        <label className="field-label" htmlFor="totalTestCount">
          Total Test Count
        </label>
        <input
          id="totalTestCount"
          type="number"
          min="0"
          step="1"
          value={form.totalTestCount}
          onChange={(e) => onChange({ totalTestCount: e.target.value })}
          placeholder={String(computedTestCount || '')}
        />
        {errors.totalTestCount ? (
          <span className="field-error field-inline-error">
            {errors.totalTestCount}
          </span>
        ) : null}
        <p className="field-help field-inline-help">
          Current calculated test count from student rows: {computedTestCount}
        </p>
      </div>

      <div className="students-table-wrap" style={{ marginTop: 18 }}>
        <table className="students-table">
          <thead>
            <tr>
              <th aria-label="Add" className="add-col" />
              <th>Name</th>
              <th>Grade Level</th>
              <th>Test</th>
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {form.students.map((student, index) => {
              const prefix = `student_${index}`;
              return (
                <tr key={student.id}>
                  <td className="add-col">
                    {index === form.students.length - 1 ? (
                      <button
                        type="button"
                        className="btn-add"
                        onClick={addStudent}
                        title="Add additional student"
                        aria-label="Add additional student"
                      >
                        +
                      </button>
                    ) : null}
                  </td>
                  <td>
                    <div className="name-pair">
                      <input
                        type="text"
                        placeholder="First"
                        value={student.firstName}
                        onChange={(e) =>
                          updateStudent(index, { firstName: e.target.value })
                        }
                      />
                      <input
                        type="text"
                        placeholder="Last"
                        value={student.lastName}
                        onChange={(e) =>
                          updateStudent(index, { lastName: e.target.value })
                        }
                      />
                    </div>
                    {(errors[`${prefix}_firstName`] ||
                      errors[`${prefix}_lastName`]) && (
                      <span className="field-error">Name required</span>
                    )}
                  </td>
                  <td>
                    <select
                      value={student.gradeLevel}
                      onChange={(e) =>
                        updateStudent(index, { gradeLevel: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      {GRADE_LEVELS.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                    {errors[`${prefix}_gradeLevel`] ? (
                      <span className="field-error">Required</span>
                    ) : null}
                  </td>
                  <td>
                    <div className="tests-cell">
                      <label className="checkbox-option">
                        <input
                          type="checkbox"
                          checked={student.tests.reasoning}
                          onChange={(e) =>
                            updateStudentTest(
                              index,
                              'reasoning',
                              e.target.checked,
                            )
                          }
                        />
                        Reasoning
                      </label>
                      <label className="checkbox-option">
                        <input
                          type="checkbox"
                          checked={student.tests.computation}
                          onChange={(e) =>
                            updateStudentTest(
                              index,
                              'computation',
                              e.target.checked,
                            )
                          }
                        />
                        Computation
                      </label>
                      {errors[`${prefix}_tests`] ? (
                        <span className="field-error">
                          {errors[`${prefix}_tests`]}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn-text danger"
                      onClick={() => removeStudent(index)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="add-hint">
        To add additional students, click the blue <strong>+</strong> on the left
        side of the form.
      </p>
    </section>
  );
}
